/**
 * @module host
 */

import hoistStatics from 'hoist-non-react-statics';
import React from 'react';
import {throwError, AppActionType} from '../actions/Error';
import {appBridgeMiddlewareProptype, ApiClientConfig, Application, Middleware} from './';

export type ReactComponent<P> = React.StatelessComponent<P> | React.ComponentClass<P>;

export interface Context {
  appBridgeMiddleware: Middleware;
}

export interface DispatchToProps {
  (app: Application): any;
}

export interface WithAppProps<Store> {
  app: Application;
  store: Store;
}

export interface RequiredProps {
  config: ApiClientConfig;
}

function getComponentName(component: any) {
  return (component && (component.displayName || component.name)) || 'Component';
}

export type StoreToProps<Store> = (obj: any) => Store;

export function withApp<Store>(
  storeToProps?: StoreToProps<Store>,
  dispatchToProps?: DispatchToProps,
) {
  return function<OwnProps>(
    WrappedComponent: ReactComponent<OwnProps>,
  ): React.ComponentClass<OwnProps & WithAppProps<Store>> {
    class AppBridgeConnector extends React.Component<any, never> {
      static contextTypes = {
        appBridgeMiddleware: appBridgeMiddlewareProptype,
      };
      static displayName = `AppBridge(${getComponentName(WrappedComponent)})`;
      static WrappedComponent = WrappedComponent;

      app: Application;
      context!: Context;
      mappedProps?: any;

      constructor(props: OwnProps & RequiredProps, context: Context) {
        super(props, context);

        if (!props.config) {
          throwError(AppActionType.MISSING_CONFIG, 'Missing required prop `config`');
        }
        const {config} = props;

        if (!context.appBridgeMiddleware || typeof context.appBridgeMiddleware.load !== 'function') {
          throwError(
            AppActionType.MISSING_APP_BRIDGE_MIDDLEWARE,
            'Missing required context `appBridgeMiddleware`. Maybe you forgot the App Bridge `<Provider>` component?',
          );
        }

        this.app = context.appBridgeMiddleware.load({
          config,
          type: 'application',
        });

        if (dispatchToProps) {
          this.mappedProps = dispatchToProps(this.app);
        }
      }

      augmentProps() {
        const appState = this.app.getState();
        const store = storeToProps ? storeToProps(appState) : appState;

        if (this.mappedProps) {
          return {
            ...this.mappedProps,
            ...this.props,
            store,
            app: this.app,
          };
        }

        return {...this.props, app: this.app, store};
      }

      render() {
        return <WrappedComponent {...this.augmentProps()} />;
      }
    }

    const FinalComponent = hoistStatics(AppBridgeConnector, WrappedComponent);

    return FinalComponent as React.ComponentClass<OwnProps & WithAppProps<Store>>;
  };
}



// WEBPACK FOOTER //
// ./src/host/decorator.tsx